import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, FileText } from 'lucide-react';
import { SmartSearchBar } from '../Layout/SmartSearchBar';
import { TextHighlighter } from './TextHighlighter';
import { ImageUpload } from './ImageUpload';
import { useAuth } from '../../hooks/useAuth';
import { useListingTemplates } from '../../hooks/useListingTemplates';
import { usePrompts } from '../../hooks/usePrompts';

export const NewListingTemplatePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { createTemplate } = useListingTemplates(user?.id || null);
  
  // We need the prompts hook for the sidebar filters, but we'll use empty filters
  const {
    filters,
    setFilters,
    getAllPlatforms,
    getAllDomains,
    getAllOutputs,
    getAllWebPlatforms,
  } = usePrompts(user?.id || null);

  const [formData, setFormData] = useState({
    name: '',
    masterTitle: '',
    masterDescription: '',
    imageUrl: null as string | null,
  });
  const [lockedElements, setLockedElements] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [variationInstruction, setVariationInstruction] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError(null);
  };

  const handleImageChange = (imageUrl: string | null) => {
    setFormData(prev => ({ ...prev, imageUrl }));
    if (error) setError(null);
  };
  
  const handleSave = async () => {
    if (!formData.name.trim() || !formData.masterTitle.trim() || !formData.masterDescription.trim()) {
      setError('Please fill in all required fields.');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const template = await createTemplate({
        name: formData.name.trim(),
        masterTitle: formData.masterTitle.trim(),
        masterDescription: formData.masterDescription.trim(),
        imageUrl: formData.imageUrl || undefined,
        lockedElements,
      });

      if (template) {
        navigate(`/listing-templates/${template.id}`);
      }
    } catch (err) {
      console.error('Error creating template:', err);
      setError(err instanceof Error ? err.message : 'Failed to create template. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSearchChange = () => {
    // No search functionality for listing templates yet
  };

  const isFormValid = formData.name.trim() && formData.masterTitle.trim() && formData.masterDescription.trim();

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-modular-canvas-fog via-modular-ivory-mist to-modular-lunar-sage 
      dark:bg-dark-gradient transition-all duration-500">
      
      {/* Floating Search Bar */}
      <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-40 w-full max-w-md lg:max-w-xl xl:max-w-2xl px-4">
        <div className="bg-white/95 dark:bg-dark-surface/95 backdrop-blur-xl border border-gray-200/60 dark:border-dark-border/40 rounded-lg shadow-sm">
          <div className="p-2">
            <SmartSearchBar
              searchQuery=""
              onSearchChange={handleSearchChange}
              prompts={[]}
              recentlyExecuted={[]}
            />
          </div>
        </div>
      </div>

      <main className="flex-1 p-6 pt-20">
        <div className="w-full">
          {/* Header */}
          <div className="flex items-center mb-8">
            <button
              onClick={() => navigate('/listing-templates')}
              className="mr-4 p-2 text-gray-600 dark:text-gray-300 hover:text-fire-600 
                dark:hover:text-modular-frosted-indigo hover:bg-modular-canvas-fog dark:hover:bg-modular-shadow-quartz/20 
                rounded-lg transition-all duration-300"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-text">
                New Listing Template
              </h1>
              <p className="text-gray-600 dark:text-dark-text-secondary">
                Create a master template for generating AI-powered listing variations
              </p>
            </div>
          </div>

          {/* Image Upload - First Section */}
          <div className="bg-white/95 dark:bg-dark-surface/95 backdrop-blur-sm rounded-xl
            border border-modular-stone-alloy/30 dark:border-modular-graphite-smoke/20 p-8 mb-8 shadow-modular-card
            dark:shadow-dark-card">
            <ImageUpload
              imageUrl={formData.imageUrl}
              onImageChange={handleImageChange}
              disabled={isSaving}
            />
          </div>

          {/* Generate Variation Section */}
          <div className="bg-white/95 dark:bg-dark-surface/95 backdrop-blur-sm rounded-xl 
            border border-modular-stone-alloy/30 dark:border-modular-graphite-smoke/20 p-8 shadow-modular-card 
            dark:shadow-dark-card">
            
            <div className="space-y-8">
              {/* Variation Instruction */}
              <div>
                <label htmlFor="variationInstruction" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Variation Instruction
                </label>
                <textarea
                  id="variationInstruction"
                  value={variationInstruction}
                  onChange={(e) => setVariationInstruction(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-dark-border rounded-lg
                    focus:outline-none focus:ring-2 focus:ring-fire-500 focus:border-transparent 
                    bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text 
                    placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300 resize-y"
                  placeholder="e.g., Tailor this listing to Egypt instead of NYC"
                  disabled={isSaving}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Describe how you want future variations to be changed. This helps the AI understand your goals.
                </p>
              </div>

              {/* Template Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Template Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-dark-border rounded-lg 
                    focus:outline-none focus:ring-2 focus:ring-fire-500 focus:border-transparent 
                    bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text 
                    placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300"
                  placeholder="e.g., NYC Ornament Template"
                  disabled={isSaving}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  A descriptive name to help you identify this template
                </p>
              </div>

              {/* Master Title */}
              <div>
                <label htmlFor="masterTitle" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Master Title *
                </label>
                <input
                  type="text"
                  id="masterTitle"
                  name="masterTitle"
                  value={formData.masterTitle}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-dark-border rounded-lg 
                    focus:outline-none focus:ring-2 focus:ring-fire-500 focus:border-transparent 
                    bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text 
                    placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300"
                  placeholder="e.g., NYC Ornament"
                  disabled={isSaving}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  The original title that will be used as the base for variations
                </p>
              </div>

              {/* Master Description */}
              <div>
                <label htmlFor="masterDescription" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Master Description *
                </label>
                <textarea
                  id="masterDescription"
                  name="masterDescription"
                  value={formData.masterDescription}
                  onChange={handleInputChange}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-dark-border rounded-lg 
                    focus:outline-none focus:ring-2 focus:ring-fire-500 focus:border-transparent 
                    bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text 
                    placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300 resize-y"
                  placeholder="e.g., Beautiful NYC ornament with depictions of the Empire State Building, red ribbon included, aluminum."
                  disabled={isSaving}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  The original description that will be used as the base for variations
                </p>
              </div>
            </div>
          </div>

          {/* Interactive Locked Elements Management */}
          {formData.masterDescription.trim() && (
            <div className="mb-8">
              <TextHighlighter
                text={formData.masterDescription}
                lockedElements={lockedElements}
                onLockedElementsChange={setLockedElements}
                label="Configure Locked Elements"
                placeholder="Enter your master description above to start locking elements..."
                disabled={isSaving}
              />
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
              <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Save Button */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-dark-border">
            <button
              onClick={() => navigate('/listing-templates')}
              disabled={isSaving}
              className="px-6 py-3 text-gray-700 dark:text-gray-300 border border-gray-300 
                dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 
                transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            
            <button
              onClick={handleSave}
              disabled={!isFormValid || isSaving}
              className="bg-fire-gradient text-white px-8 py-3 rounded-lg hover:shadow-fire-glow 
                focus:outline-none focus:ring-2 focus:ring-fire-500 focus:ring-offset-2 
                transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed 
                shadow-fire-card transform hover:scale-105 active:scale-95 
                flex items-center font-semibold min-w-[180px] justify-center"
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Save className="h-5 w-5 mr-2" />
                  Create Template
                </>
              )}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};